import _isEqual from "lodash/isEqual";
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { default as ReactTable, default as useTable } from "react-table";
import withFixedColumns from "react-table-hoc-fixed-columns";
import "react-table/react-table.css";
import { Col, Container, Row } from "reactstrap";
import { Pagination } from "../../components";
import { DialogContext } from "../../store/context/DialogContext";
import { I18nContext } from "../../store/context/i18nContext";
import { doGET, doPUT } from "../../util/HttpUtil";
import {
  buildRecordTestIdSegment,
  normalizeTestIdSegment,
  resolveAutomationTestId,
} from "../InputField/testId";
import DataDridUIPreferences from "./DataDridUIPreferences";
import UIPrefService from "./UIPrefService";
import "./styles.css";

const ReactTableFixedColumns = withFixedColumns(ReactTable);

const LOADING_DATA = [
  { _id: "loader-1", dataGridLoader: true },
  { _id: "loader-2", dataGridLoader: true },
  { _id: "loader-3", dataGridLoader: true },
  { _id: "loader-4", dataGridLoader: true },
];

const getRecordId = (row = {}) => (
  row?._id ??
  row?.id ??
  row?.applicationFormId ??
  row?.application_form_id ??
  row?.formId ??
  row?.form_id ??
  row?.applicationId ??
  row?.application_id ??
  row?.accountId ??
  row?.account_id ??
  row?.documentId ??
  row?.document_id ??
  row?.userId ??
  row?.user_id ??
  row?.roleId ??
  row?.role_id ??
  row?.uuid
);

const parsePreferenceValue = (rawValue) => {
  if (!rawValue) {
    return null;
  }

  let parsed = rawValue;
  for (let i = 0; i < 2 && typeof parsed === "string"; i += 1) {
    try {
      parsed = JSON.parse(parsed);
    } catch (error) {
      return null;
    }
  }

  if (Array.isArray(parsed)) {
    return { hiddenColumns: parsed };
  }

  if (parsed && typeof parsed === "object") {
    if (Array.isArray(parsed.visibleColumns)) {
      return { visibleColumns: parsed.visibleColumns };
    }

    if (Array.isArray(parsed.hiddenColumns)) {
      return { hiddenColumns: parsed.hiddenColumns };
    }
  }

  return null;
};

const applyColumnPreferences = (columns = [], preference) => {
  if (!preference) {
    return columns;
  }

  const visibleColumns = Array.isArray(preference.visibleColumns)
    ? preference.visibleColumns
    : null;
  const hiddenColumns = Array.isArray(preference.hiddenColumns)
    ? preference.hiddenColumns
    : null;

  const nextColumns = columns.map((column) => {
    if (column?.show === false) {
      return column;
    }

    if (column?.required || column?.accessor === "action") {
      return { ...column, show: true };
    }

    if (visibleColumns) {
      return { ...column, show: visibleColumns.includes(column.title) };
    }

    if (hiddenColumns) {
      return { ...column, show: !hiddenColumns.includes(column.title) };
    }

    return column;
  });

  if (!visibleColumns) {
    return nextColumns;
  }

  return [...nextColumns].sort((first, second) => {
    const firstIndex = visibleColumns.indexOf(first.title);
    const secondIndex = visibleColumns.indexOf(second.title);

    if (firstIndex === -1 && secondIndex === -1) return 0;
    if (firstIndex === -1) return 1;
    if (secondIndex === -1) return -1;
    return firstIndex - secondIndex;
  });
};

const buildPreferencePayload = (columns = [], visibleColumns = []) => {
  const configuredTitles = columns
    .filter((column) => column?.accessor !== "check" && column?.accessor !== "action")
    .map((column) => column.title);
  const requiredTitles = columns
    .filter((column) => column?.required && column?.accessor !== "check" && column?.accessor !== "action")
    .map((column) => column.title);
  const selectedTitles = visibleColumns?.length
    ? visibleColumns.map((column) => column.title)
    : configuredTitles;
  const visibleTitles = Array.from(new Set([...selectedTitles, ...requiredTitles]));

  return JSON.stringify({
    visibleColumns: visibleTitles,
    hiddenColumns: configuredTitles.filter((title) => !visibleTitles.includes(title)),
  });
};

const normalizeColumnToken = (value) => String(value || "")
  .trim()
  .toLowerCase()
  .replace(/[_-]+/g, " ");

const isBrokersGridKey = (value) => {
  const key = normalizeColumnToken(value).replace(/\s+/g, ".");
  return key === "brokers" || key === "brokers.grid" || key === "broker.grid";
};

const hasBrokersGridSignature = (columns = []) => {
  const titles = new Set(columns.map((column) => normalizeColumnToken(column?.title)).filter(Boolean));
  const accessors = new Set(columns.map((column) => normalizeColumnToken(column?.accessor)).filter(Boolean));

  const hasBrokerColumn = titles.has("broker") ||
    accessors.has("broker") ||
    accessors.has("displayname") ||
    accessors.has("display name") ||
    accessors.has("legalname") ||
    accessors.has("legal name");
  const hasCyclosColumn = titles.has("cyclos user") ||
    titles.has("cyclos user id") ||
    accessors.has("cyclosuserid") ||
    accessors.has("cyclosuser id") ||
    accessors.has("cyclos user id");
  const hasBrandingColumn = titles.has("branding") ||
    accessors.has("branding") ||
    accessors.has("logo");
  const hasUpdatedColumn = titles.has("updated") ||
    accessors.has("updatedat") ||
    accessors.has("updated at");

  return hasBrokerColumn && hasCyclosColumn && hasBrandingColumn && hasUpdatedColumn;
};

const stripFixedColumnSizing = (column) => {
  if (!column || column?.accessor === "check") {
    return column;
  }

  const columnWithoutSizing = { ...column };
  delete columnWithoutSizing.width;
  delete columnWithoutSizing.minWidth;
  delete columnWithoutSizing.maxWidth;
  return columnWithoutSizing;
};

const DataGrid = ({
  data = [],
  renderLastCol = () => null,
  hideConfiguration,
  headers = { columns: [] },
  onSelectChange = () => {},
  selectedIDs = [],
  gridLoading,
  total = 0,
  page = 1,
  rowsPerPage = 40,
  rowHeight = null,
  fixedGridHeight = null,
  hideCheckboxes = false,
  onPaginationChange = () => {},
  uiPreference,
  hidePagination,
  hidePaginationButShowUIPref = false,
  fullHeight,
  noRecordMessage = "No records available.",
  paginationMaxLimit = 400,
  onSaveUiPref = () => false,
  gridClassName = "",
  tableClassName = "",
  paginationClassName = "",
  testId,
  gridId,
  getRowTestId,
  filterBarConfig = null,
  preserveRowsOnLoading = true,
}) => {
  const lastStableDataRef = useRef([]);
  const gridWrapperRef = useRef(null);
  const sourceData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  useEffect(() => {
    if (!gridLoading) {
      lastStableDataRef.current = sourceData.length ? sourceData : [];
    }
  }, [gridLoading, sourceData]);

  const memoizedData = useMemo(() => {
    if (!gridLoading) {
      return sourceData.length ? sourceData : [];
    }

    if (sourceData.length) {
      return sourceData;
    }

    if (preserveRowsOnLoading && lastStableDataRef.current.length) {
      return lastStableDataRef.current;
    }

    return LOADING_DATA;
  }, [sourceData, gridLoading, preserveRowsOnLoading]);
  const { t } = useContext(I18nContext);
  const { showMessage } = useContext(DialogContext);
  const [showGridConfigModal, setShowGridConfigModal] = useState(false);
  const [uiPrefData, setUiPrefData] = useState({ columns: [] });
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [gridHeight, setGridHeight] = useState();
  const [fadeClass, setFadeClass] = useState("fade-enter");
  const updateGridHeight = useCallback((nextHeight) => {
    setGridHeight((currentHeight) => (currentHeight === nextHeight ? currentHeight : nextHeight));
  }, []);
  const gridTestId = resolveAutomationTestId(testId, gridId, uiPreference, "data-grid");
  const filterPreferenceConfig = filterBarConfig?.preferences ?? null;
  const shouldUseFluidBrokerColumns = useMemo(() => (
    [uiPreference, gridId, testId].some(isBrokersGridKey) ||
    hasBrokersGridSignature(headers?.columns)
  ), [gridId, headers?.columns, testId, uiPreference]);
  const normalizeGridColumns = useCallback((columns = []) => (
    shouldUseFluidBrokerColumns ? columns.map(stripFixedColumnSizing) : columns
  ), [shouldUseFluidBrokerColumns]);
  const hasFilterPreferenceTab = Boolean(
    filterPreferenceConfig?.enabled &&
    filterPreferenceConfig?.canConfigure &&
    Array.isArray(filterPreferenceConfig?.structure)
  );

  const getReadableRowId = useCallback((row = {}, fallbackIndex = 0) => {
    const customRowId = typeof getRowTestId === "function"
      ? getRowTestId(row, fallbackIndex)
      : undefined;

    return normalizeTestIdSegment(
      customRowId || buildRecordTestIdSegment(row, { fallbackPrefix: "record", fallback: `row-${fallbackIndex + 1}` }),
      `row-${fallbackIndex + 1}`
    );
  }, [getRowTestId]);

  const checkAllSelected = useCallback((rows = [], selected = []) => {
    const rowIds = rows.map(getRecordId).filter(Boolean);
    return Boolean(rowIds.length && selected?.length && rowIds.every((id) => selected.includes(id)));
  }, []);

  const setDefaultColumns = useCallback((checkBoxColumn) => {
    const nextColumns = normalizeGridColumns(headers?.columns || []);
    setUiPrefData({
      ...headers,
      columns: !hideCheckboxes ? [checkBoxColumn, ...nextColumns] : [...nextColumns],
    });
    setSelectedColumns(nextColumns.filter((column) => column?.accessor !== "action" && !column?.required && column?.show !== false));
  }, [headers, hideCheckboxes, normalizeGridColumns]);

  const prepareUIPref = useCallback(async (allSelected) => {
    const currentPageIds = memoizedData.map(getRecordId).filter(Boolean);
    const checkBoxColumn = {
      title: (
        <input
          type="checkbox"
          className="me-2"
          data-testid={`${gridTestId}-select-all`}
          style={{ marginLeft: "4px" }}
          checked={allSelected}
          onChange={() => {
            const selectedOutsideCurrentPage = Array.from(
              new Set((selectedIDs || []).filter((item) => !currentPageIds.includes(item)))
            );
            const selected = allSelected
              ? selectedOutsideCurrentPage
              : Array.from(new Set([...selectedIDs, ...currentPageIds]));

            onSelectChange(selected);
          }}
        />
      ),
      accessor: "check",
      fixed: "left",
      width: 50,
      show: true,
      required: true,
    };

    if (!headers?.columns) {
      setUiPrefData({
        ...headers,
        columns: hideCheckboxes ? [] : [checkBoxColumn],
      });
      return;
    }

    if (!uiPreference) {
      setDefaultColumns(checkBoxColumn);
      return;
    }

    try {
      const response = UIPrefService?.uiPrefs[uiPreference]
        ? { status: 200, data: { value: UIPrefService.uiPrefs[uiPreference] } }
        : await doGET(`/api/ui-preference/detail?id=${uiPreference}`);
      const rawPreference = response?.data?.value ?? "";
      UIPrefService?.updatePrefs(uiPreference, rawPreference || "[]");
      const preference = response?.status === 200 ? parsePreferenceValue(rawPreference) : null;
      const updatedColumns = normalizeGridColumns(applyColumnPreferences(headers.columns, preference));

      setSelectedColumns(
        updatedColumns.filter((column) => (
          column?.accessor !== "action" &&
          !column?.required &&
          column?.show !== false
        ))
      );
      setUiPrefData({
        ...headers,
        columns: !hideCheckboxes ? [checkBoxColumn, ...updatedColumns] : [...updatedColumns],
      });
    } catch (error) {
      setDefaultColumns(checkBoxColumn);
    }
  }, [gridTestId, headers, hideCheckboxes, memoizedData, normalizeGridColumns, onSelectChange, selectedIDs, setDefaultColumns, uiPreference]);

  const reSizeTable = useCallback(() => {
    const gridRoot = gridWrapperRef.current;
    const divElements = Array.from(
      gridRoot?.getElementsByClassName("ReactTable") || document.getElementsByClassName("ReactTable")
    );
    if (!divElements.length) {
      return;
    }

    const targetDiv = divElements.find((div) => {
      const rect = div?.getBoundingClientRect();
      return rect?.top >= 0 && rect?.bottom <= window.innerHeight;
    }) || divElements[0];
    const rect = targetDiv.getBoundingClientRect();
    const newHeight = rect.height + window.innerHeight - rect.bottom - 75;
    const insidePaneHeight = rect.height + window.innerHeight - rect.bottom - 130;
    const tableElements = gridRoot?.querySelectorAll(".ReactTable .rt-table") || document.querySelectorAll(".ReactTable .rt-table");
    const tableMaxHeight = tableElements[0]?.scrollHeight + 6;
    const effectiveRowHeight = rowHeight ?? 42;
    const tableHeight = fullHeight
      ? insidePaneHeight
      : newHeight > tableMaxHeight
        ? tableMaxHeight
        : newHeight;

    if (fixedGridHeight) {
      updateGridHeight(fixedGridHeight);
      return;
    }

    if (tableHeight < memoizedData?.length * effectiveRowHeight) {
      updateGridHeight(hidePagination ? tableHeight + effectiveRowHeight : tableHeight);
    } else {
      updateGridHeight((memoizedData?.length * effectiveRowHeight) + effectiveRowHeight + 8);
    }
  }, [fixedGridHeight, fullHeight, hidePagination, memoizedData, rowHeight, updateGridHeight]);

  useEffect(() => {
    window.addEventListener("resize", reSizeTable);
    reSizeTable();
    return () => window.removeEventListener("resize", reSizeTable);
  }, [reSizeTable]);

  useEffect(() => {
    prepareUIPref(checkAllSelected(memoizedData, selectedIDs));
  }, [checkAllSelected, memoizedData, prepareUIPref, selectedIDs]);

  useEffect(() => {
    setFadeClass("fade-enter-active");
  }, []);

  const renderGridCell = useCallback((dataObj, field, renderer, rowIndex, pageSize) => {
    if (field === "check") {
      if (dataObj?.dataGridLoader) return <div className="data-grid-skeleton-loader" />;
      return renderCheckboxCell({
        dataObj,
        selectedIDs,
        testId: `${gridTestId}-select-${getReadableRowId(dataObj, rowIndex)}`,
        onSelectChange,
      });
    }

    if (field === "action") {
      if (dataObj?.dataGridLoader) return <div className="data-grid-skeleton-loader" />;
      return renderLastCol(dataObj, rowIndex, pageSize);
    }

    return dataObj?.dataGridLoader
      ? <div className="data-grid-skeleton-loader" />
      : renderer
        ? renderer(dataObj, rowIndex, pageSize)
        : dataObj[field];
  }, [getReadableRowId, gridTestId, onSelectChange, renderLastCol, selectedIDs]);

  const getColumns = useCallback(() => (
    uiPrefData?.columns?.map((column) => {
      const accessor = column?.accessor;
      const cellClassName = [
        "rw-data-grid__cell",
        "height-100",
        "w-100",
        "d-flex",
        "align-items-center",
        accessor === "action" ? "rw-data-grid__cell--action" : "",
        accessor === "check" ? "rw-data-grid__cell--check" : "",
        column?.cellClassName || "",
      ].filter(Boolean).join(" ");

      return {
        Header: accessor === "check" ? column?.title : `${t(column.title)}${column.required ? "*" : ""}`,
        accessor,
        fixed: column.fixed,
        width: column.width,
        show: column.show,
        headerClassName: column?.headerClassName,
        className: column?.className,
        id: normalizeTestIdSegment(accessor, "column"),
        Cell: (row) => {
          const cellTestId = accessor ? `${gridTestId}-cell-${normalizeTestIdSegment(accessor)}` : undefined;

          return (
            <div className={cellClassName} data-testid={cellTestId}>
              {renderGridCell(row.original, accessor, column.renderer, row?.index, row?.pageSize)}
            </div>
          );
        },
      };
    })
  ), [gridTestId, renderGridCell, t, uiPrefData]);

  const saveGridConfig = useCallback(async (payload) => {
    const visibleColumns = Array.isArray(payload) ? payload : payload?.visibleColumns ?? [];
    const nextFilterPreferences = Array.isArray(payload) ? null : payload?.filterPreferences;

    try {
      const preferenceValue = buildPreferencePayload(uiPrefData?.columns, visibleColumns);
      const saveTasks = [
        uiPreference
          ? doPUT("/api/ui-preference/update", { key: uiPreference, value: preferenceValue })
          : Promise.resolve(),
      ];

      if (hasFilterPreferenceTab && nextFilterPreferences) {
        saveTasks.push(filterPreferenceConfig?.onSave?.(nextFilterPreferences));
      }

      await Promise.all(saveTasks);
      if (uiPreference) UIPrefService?.updatePrefs(uiPreference, preferenceValue);
      showMessage?.("Preference updated successfully", "Success");
      setShowGridConfigModal(false);
      prepareUIPref(checkAllSelected(memoizedData, selectedIDs));
      onSaveUiPref();
    } catch (error) {
      console.error(error);
    }
  }, [
    checkAllSelected,
    filterPreferenceConfig,
    hasFilterPreferenceTab,
    memoizedData,
    onSaveUiPref,
    prepareUIPref,
    selectedIDs,
    showMessage,
    uiPrefData?.columns,
    uiPreference,
  ]);

  const { getTableProps } = useTable;
  const getTable = useCallback((...args) => ({
    ...(getTableProps && getTableProps(...args)),
    style: { height: 801, overflow: "auto", scrollTop: 300 },
  }), [getTableProps]);

  const tablePageSize = memoizedData.length > rowsPerPage ? rowsPerPage : memoizedData.length;
  const reactTableProps = useMemo(() => ({
    data: memoizedData,
    columns: getColumns(),
    defaultPageSize: tablePageSize,
    pageSize: tablePageSize,
    className: `-striped -highlight rw-data-grid ${tableClassName}`.trim(),
    showPagination: false,
    getTableProps: getTable,
    sortable: false,
    getTrProps: (_state, rowInfo) => ({
      "data-testid": rowInfo?.original
        ? `${gridTestId}-row-${getReadableRowId(rowInfo.original, rowInfo.index)}`
        : undefined,
      "data-record-id": rowInfo?.original ? getRecordId(rowInfo.original) : undefined,
    }),
  }), [getColumns, getReadableRowId, getTable, gridTestId, memoizedData, tableClassName, tablePageSize]);

  const gridStyle = rowHeight ? { "--rw-grid-row-height": `${rowHeight}px` } : undefined;
  const showGridFooter = !hidePagination || hidePaginationButShowUIPref || (uiPreference && !hideConfiguration);
  const showingLoaderRows = memoizedData.some((row) => row?.dataGridLoader);
  const showRefreshIndicator = gridLoading && !showingLoaderRows;

  if (!memoizedData?.length) {
    return (
      <Container className="rw-data-grid__empty" data-testid={`${gridTestId}-empty`}>
        <Row>
          <Col md={12}>
            <div className="text-center">
              <h2 className="display-7 mt-4 mb-3 rw-data-grid__empty-title">
                {noRecordMessage ?? t("No records available.")}
              </h2>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <div
      ref={gridWrapperRef}
      className={`${gridClassName} flex-1 zr_data_grid_${fadeClass} ${showRefreshIndicator ? "rw-data-grid--refreshing" : ""}`.trim()}
      style={gridStyle}
      data-testid={gridTestId}
    >
      {showRefreshIndicator ? (
        <div className="rw-data-grid__refresh-indicator" role="status" aria-live="polite">
          <span className="rw-data-grid__refresh-spinner" aria-hidden="true" />
          Refreshing
        </div>
      ) : null}

      <ReactTableFixedColumns
        key={reactTableProps?.defaultPageSize}
        {...reactTableProps}
        style={{ height: gridHeight }}
      />

      {showGridFooter ? (
        <div className={`rw-data-grid__pagination ${paginationClassName}`.trim()} data-testid={`${gridTestId}-pagination`}>
          {!hidePagination ? (
            <Pagination
              page={page}
              rowsPerPage={rowsPerPage}
              total={total}
              uiPreference={uiPreference}
              onChange={onPaginationChange}
              paginationMaxLimit={paginationMaxLimit}
              testId={`${gridTestId}-pagination`}
            />
          ) : <span />}

          {!hideConfiguration && uiPreference ? (
            <button
              type="button"
              className="rw-data-grid__settings"
              onClick={() => setShowGridConfigModal(true)}
              aria-label="Configure grid"
              data-testid={`${gridTestId}-settings`}
            >
              <i className="dripicons-gear" aria-hidden="true" />
            </button>
          ) : null}

          <DataDridUIPreferences
            data={headers?.columns?.filter((column) => (
              column?.accessor !== "action" &&
              !column?.required &&
              column?.show !== false
            ))}
            isOpen={showGridConfigModal}
            onClose={() => setShowGridConfigModal(false)}
            onSave={saveGridConfig}
            value={selectedColumns?.filter((column) => (
              column?.accessor !== "action" &&
              !column?.required &&
              column?.show !== false
            ))}
            onChange={setSelectedColumns}
            title={hasFilterPreferenceTab ? "Grid Settings" : "Select Columns"}
            filterConfig={hasFilterPreferenceTab ? filterPreferenceConfig : null}
            testId={`${gridTestId}-preferences`}
          />
        </div>
      ) : null}
    </div>
  );
};

const renderCheckboxCell = ({ dataObj, selectedIDs, onSelectChange, testId }) => {
  const recordId = getRecordId(dataObj);

  return (
    <input
      type="checkbox"
      className="ms-2"
      data-testid={testId}
      data-record-id={recordId}
      checked={(selectedIDs ?? []).includes(recordId)}
      onChange={() => {
        const newSelectedIDs = (selectedIDs ?? []).includes(recordId)
          ? (selectedIDs ?? []).filter((id) => id !== recordId)
          : [...(selectedIDs ?? []), recordId];

        onSelectChange(newSelectedIDs);
      }}
    />
  );
};

export default memo(DataGrid, (prev, next) => (
  _isEqual(prev?.data, next?.data) &&
  _isEqual(prev?.headers, next?.headers) &&
  prev?.rowsPerPage === next?.rowsPerPage &&
  prev?.page === next?.page &&
  prev?.total === next?.total &&
  prev?.gridLoading === next?.gridLoading &&
  prev?.hideCheckboxes === next?.hideCheckboxes &&
  prev?.preserveRowsOnLoading === next?.preserveRowsOnLoading &&
  prev?.hidePagination === next?.hidePagination &&
  prev?.hideConfiguration === next?.hideConfiguration &&
  prev?.rowHeight === next?.rowHeight &&
  prev?.fixedGridHeight === next?.fixedGridHeight &&
  prev?.gridClassName === next?.gridClassName &&
  prev?.tableClassName === next?.tableClassName &&
  _isEqual(prev?.selectedIDs, next?.selectedIDs) &&
  prev?.renderLastCol === next?.renderLastCol &&
  prev?.getRowTestId === next?.getRowTestId
));
